import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { GetInstrumentInstrumentNameTradeHistory200ResponseTradeHistoryInner } from "../../codegen-api";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { WebsocketContext } from "../../contexts/WebsocketAuthContext";
import { TimePeriodEnum } from "../../interfaces/TimePeriod";
import { toNanosString } from "../../utils/timestampUnits";
import { knownPrice } from "../../utils/unknownNumbers";
import { forEachFrame, readFrame } from "./parseFrame";
import { useInstrumentTradeHistory } from "../api/tradeHistory/useInstrumentTradeHistory";
import { IWSSTradesResponse } from "./model/trades";
import {
  convertOrderbookQuantity,
  toDisplayTradePrice,
} from "../../utils/rawConversion";
import { useMarketDecimals } from "../useMarketDecimals";
import {
  clearScheduledRevalidations,
  scheduleRevalidations,
} from "../../utils/revalidation";

const tradesChannel = "trades";

const MAX_TRADES = 50;

// Heuristic: treat values that look like raw 18-decimal integers as raw.
const looksRawInteger = (val: string | number | undefined): boolean => {
  if (val === undefined || val === null) return false;
  const s = String(val);
  if (s.includes(".")) return false;
  // Raw on-chain ints for 18-decimal tokens are typically very large (>1e9)
  return /^\d{10,}$/.test(s);
};

const hasValidPositiveNumber = (val: string | number | undefined): boolean => {
  const num = Number(val);
  return Number.isFinite(num) && num > 0;
};

const useTradesWSS = (
  instrumentName?: string,
  period: TimePeriodEnum = TimePeriodEnum.WEEKLY,
  populatePastData: boolean = false
) => {
  const { triggerSubscribe, triggerUnsubscribe, lastMessages } =
    useContext(WebsocketContext);
  const { activePerpMarkets, activeSpotMarkets } = useContext(MarketInstrumentContext);
  const decimals = useMarketDecimals(instrumentName);
  // The market's own price, used only to tell a raw trade price from a decimal
  // one (see toDisplayTradePrice). Read once per market change is enough: the
  // two candidates it separates differ by 10^quoteDecimals.
  const referencePrice = useMemo(() => {
    const market = [...(activePerpMarkets ?? []), ...(activeSpotMarkets ?? [])].find(
      (m) => m.instrument_name === instrumentName
    );
    // A MAGNITUDE reference only, never displayed or traded at: either price
    // tells a raw integer from a decimal one, since the candidates differ by
    // 10^quoteDecimals. Unknown (null / "0") prices are skipped.
    return knownPrice(market?.mark_price) ?? knownPrice(market?.index_price);
  }, [activePerpMarkets, activeSpotMarkets, instrumentName]);
  const {
    data: instrumentTradeHistoryData,
    isValidating,
    mutate: mutateInstrumentTradeHistory,
  } =
    useInstrumentTradeHistory(
      populatePastData ? instrumentName : undefined,
      period
    );
  const seenTradeIdsRef = useRef<Record<string, Set<string>>>({});
  const revalidationTimersRef = useRef<number[]>([]);
  const lastRevalidationRef = useRef(0);
  const [tradeHistoriesKey, setTradeHistoryKey] = useState<{
    [
      instrumentName: string
    ]: GetInstrumentInstrumentNameTradeHistory200ResponseTradeHistoryInner[];
  }>({});

  const tradeHistories = useMemo(
    () => (instrumentName ? tradeHistoriesKey[instrumentName] || [] : []),
    [instrumentName, tradeHistoriesKey]
  );

  useEffect(() => {
    if (!instrumentName) {
      return undefined;
    }

    const data = [`${tradesChannel}:${instrumentName}`];
    triggerSubscribe("subscribe", data);
    return () => triggerUnsubscribe(data);
  }, [instrumentName, triggerSubscribe, triggerUnsubscribe]);

  const revalidateInstrumentTradeHistorySoon = useCallback(() => {
    if (!instrumentName || !populatePastData) {
      return;
    }
    scheduleRevalidations(
      mutateInstrumentTradeHistory,
      revalidationTimersRef,
      lastRevalidationRef,
      {
        delays: [8000],
        throttleMs: 6000,
      }
    );
  }, [instrumentName, mutateInstrumentTradeHistory, populatePastData]);

  useEffect(
    () => () => clearScheduledRevalidations(revalidationTimersRef),
    []
  );

  // Receives messages and updates state
  useEffect(() => {
    if (!lastMessages || !instrumentName) return;
    // Converting here is DESTRUCTIVE: the scaled values are pushed into state
    // below and the seen-trade-id set stops the same frames being processed
    // again, so a trade converted at the wrong scale stays wrong for the life
    // of the page. Returning before any id is recorded means these frames are
    // simply folded again once /markets lands.
    if (!decimals) return;

    const expectedChannel = `${tradesChannel}:${instrumentName}`;
    const expectedLower = expectedChannel.toLowerCase();
    const { baseDecimals, quoteDecimals } = decimals;

    const nextTrades: GetInstrumentInstrumentNameTradeHistory200ResponseTradeHistoryInner[] = [];

    forEachFrame(lastMessages, "useTradesWSS", (lastMessage) => {
      const parsed = readFrame<IWSSTradesResponse>(lastMessage);
      if (!parsed) return;

      const { data, channel } = parsed;
      if (typeof channel !== "string" || !data) return;
      if (channel !== expectedChannel && channel.toLowerCase() !== expectedLower) {
        return;
      }

      // Streamer-mapped trade shape OR raw indexer trade.
      // Raw indexer:   { id, poolId, price, quantity, side, timestamp, transactionId, ... }
      // Streamer: { trade_id, instrument_name, price, amount, side, created_timestamp, transaction_id }
      const rawPrice =
        (data as any).price ??
        (data as any).executionPrice ??
        (data as any).execution_price;
      const rawAmount =
        (data as any).amount ??
        (data as any).quantity ??
        (data as any).volumeBase;
      const tradeId =
        (data as any).trade_id ??
        (data as any).id ??
        (data as any).tradeId;
      // The REST endpoint returns `created_timestamp` in nanoseconds (19 digits)
      // while the WS feed sends Unix seconds (10 digits). Normalise WS values
      // to nanoseconds so the merged-and-sorted list orders WS fills above older
      // REST fills (otherwise Number(seconds) << Number(nanoseconds) and the
      // newest fills sink to the bottom of the list). The unit is read from the
      // digit count, so a microsecond value is no longer taken for ns (FE-16).
      const tsRaw =
        (data as any).created_timestamp ??
        (data as any).timestamp ??
        Math.floor(Date.now() / 1000);
      const ts = toNanosString(tsRaw) ?? (toNanosString(Date.now()) as string);
      const txId =
        (data as any).transaction_id ?? (data as any).transactionId;
      const side = (data as any).side;
      const uniqueTradeId = String(
        tradeId || `${txId || ""}:${rawPrice || ""}:${rawAmount || ""}:${ts}:${side || ""}`
      );
      const seenForInstrument =
        seenTradeIdsRef.current[instrumentName] ||
        (seenTradeIdsRef.current[instrumentName] = new Set<string>());
      if (seenForInstrument.has(uniqueTradeId)) {
        return;
      }
      seenForInstrument.add(uniqueTradeId);
      if (seenForInstrument.size > 500) {
        const oldest = seenForInstrument.values().next().value;
        if (oldest) seenForInstrument.delete(oldest);
      }

      // Convert raw → display only when the value is raw (see toDisplayTradePrice).
      const displayPrice =
        toDisplayTradePrice(rawPrice, quoteDecimals, referencePrice) || "0";
      const displayAmount = looksRawInteger(rawAmount)
        ? convertOrderbookQuantity(rawAmount as string, baseDecimals)
        : (rawAmount ?? "0").toString();

      if (
        !hasValidPositiveNumber(displayPrice) ||
        !hasValidPositiveNumber(displayAmount)
      ) {
        return;
      }

      const trade: GetInstrumentInstrumentNameTradeHistory200ResponseTradeHistoryInner = {
        ...(data as any),
        trade_id: uniqueTradeId,
        instrument_name: instrumentName,
        price: displayPrice,
        amount: displayAmount,
        side,
        created_timestamp: ts,
        transaction_id: txId,
      };

      nextTrades.push(trade);
    });

    if (!nextTrades.length) {
      return;
    }

    setTradeHistoryKey((ths) => {
      const existing = ths[instrumentName] || [];
      const existingIds = new Set(existing.map((t) => t.trade_id));
      const dedupedNext = nextTrades.filter(
        (trade) => !trade.trade_id || !existingIds.has(trade.trade_id)
      );
      if (!dedupedNext.length) return ths;
      return {
        ...ths,
        [instrumentName]: [...existing, ...dedupedNext].slice(-MAX_TRADES),
      };
    });
    revalidateInstrumentTradeHistorySoon();
  }, [decimals, instrumentName, lastMessages, referencePrice, revalidateInstrumentTradeHistorySoon]);

  const aggregatedTradeHistories = useMemo(() => {
    // REST rows get the same price normalisation as websocket rows, so every
    // consumer (trade panel, order book "Last") receives a display price.
    //
    // No decimals, no rows. This used to pass the value through untouched,
    // with a comment arguing a raw integer is "better left visibly raw than
    // silently mis-scaled" -- which is wrong twice over. A raw 18-decimal
    // integer is NOT visibly raw to a trader: 76805100000000000000000 reads as
    // a price, and it is the most dangerous number this app can print. And it
    // is inconsistent with every sibling call site, all of which drop to
    // unknown. An empty list is a loading state the UI already handles; a
    // fabricated price is not.
    const histories = decimals
      ? (instrumentTradeHistoryData || []).map((h) => ({
          ...h,
          price: toDisplayTradePrice(h.price, decimals.quoteDecimals, referencePrice),
        }))
      : [];

    tradeHistories.forEach((h) => {
      const exists = histories.some((hist) => hist.trade_id === h.trade_id);
      if (!exists) {
        histories.push(h);
      }
    });

    return histories.sort(
      (a, b) => Number(b.created_timestamp) - Number(a.created_timestamp)
    );
  }, [decimals, instrumentTradeHistoryData, referencePrice, tradeHistories]);

  return {
    data: aggregatedTradeHistories,
    loading: !instrumentTradeHistoryData && isValidating,
  };
};

export default useTradesWSS;
